/**
 * Resolve origin/destination on dispatch unassigned rows and event modals.
 * Unassigned trucking rows are Leg documents (`route.origin`). Older job-shaped
 * rows nest the same data under `legs[0].route.origin`.
 */

const ORIGIN_KEYS = ["origin", "startLocation", "pickupLocation"];
const DESTINATION_KEYS = [
  "destination",
  "endLocation",
  "deliveryLocation",
  "dropoffLocation",
];

/**
 * 24-char hex only. mongoose ObjectId.isValid also matches 12-char addresses.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isObjectIdHex(value) {
  return typeof value === "string" && /^[a-fA-F0-9]{24}$/.test(value);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function toIdString(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "undefined" || trimmed === "null") return "";
    if (trimmed === "[object Object]") return "";
    return trimmed;
  }
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value.toHexString === "function") return value.toHexString();
  if (value._id) return toIdString(value._id);
  if (value.id && value.id !== value) return toIdString(value.id);
  const asString = String(value);
  return asString && asString !== "[object Object]" ? asString : "";
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isPopulatedLocation(value) {
  if (value == null || value === "") return false;
  if (typeof value === "string") return !isObjectIdHex(value);
  if (typeof value !== "object") return false;
  return Boolean(
    value.name ||
      value.address ||
      value.phone ||
      value.note ||
      value.precall ||
      value.receivingHours ||
      value.fullAddress
  );
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isBareLocationRef(value) {
  if (value == null || value === "") return false;
  if (typeof value === "string") return isObjectIdHex(value);
  if (typeof value !== "object") return false;
  if (isPopulatedLocation(value)) return false;
  if (value._bsontype === "ObjectId" || typeof value.toHexString === "function") {
    return true;
  }
  const id = toIdString(value);
  return isObjectIdHex(id) && !value.name && !value.address;
}

function firstUsableLocation(source, keys) {
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    const value = source[key];
    if (isPopulatedLocation(value)) return value;
    if (value && !isBareLocationRef(value) && typeof value === "string") {
      return value;
    }
  }
  return null;
}

/**
 * @param {object} row - Leg document, job document, or scheduler event
 * @param {"origin"|"destination"} side
 * @returns {object|string|null}
 */
export function resolveLegRouteLocation(row, side = "origin") {
  const keys = side === "destination" ? DESTINATION_KEYS : ORIGIN_KEYS;
  const candidates = [
    row,
    row?.route,
    row?.legs?.[0],
    row?.legs?.[0]?.route,
  ];

  for (const candidate of candidates) {
    const location = firstUsableLocation(candidate, keys);
    if (location) return location;
  }

  return null;
}

/**
 * Prefer a real mileage value including 0. `if (mileage)` hid 0 as N/A.
 * @param {object|null|undefined} route
 * @returns {number|null}
 */
export function getRouteMileage(route) {
  if (!route || typeof route !== "object") return null;
  const candidates = [
    route.mileage,
    route.defaultMileage,
    route.customMileage,
    route.billedMiles,
    route.estimatedDistance,
  ];
  const value = candidates.find((item) => item != null && item !== "");
  if (value == null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatAddressText(address) {
  if (!address) return "";
  if (typeof address === "string") {
    return isObjectIdHex(address) ? "" : address;
  }
  if (typeof address !== "object") return "";
  if (isBareLocationRef(address)) return "";
  if (address.fullAddress) return String(address.fullAddress);
  const street = address.address || address.street || address.line1;
  const zip = address.zipcode || address.zipCode || address.postalCode;
  return [street, address.city, address.state, zip].filter(Boolean).join(", ");
}

/**
 * Plain-text pickup/dropoff label. Unlike formatAddress, missing address does
 * not return "Address not available" so callers can fall back to a name.
 * @param {unknown} location
 * @returns {string}
 */
export function formatLocationLabel(location) {
  if (!location) return "";
  if (typeof location === "string") {
    return isObjectIdHex(location) ? "" : location;
  }
  if (typeof location !== "object" || isBareLocationRef(location)) return "";

  const name = location.name || location.title || "";
  const addressText = formatAddressText(location.address || location);
  if (name && addressText && addressText !== name) {
    return `${name} — ${addressText}`;
  }
  return name || addressText || "";
}

/**
 * GPS / shift pin that is safe to plot. Default GeoJSON [0,0] is not a yard.
 * @param {object|null|undefined} location
 * @returns {boolean}
 */
export function isPlottableLatLng(location) {
  const lat = Number(location?.lat);
  const lng = Number(location?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}
